<template>
  <div class="outlook-profile-connect border rounded p-3 mb-3">
    <div class="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
      <div>
        <h5 class="h6 mb-1">
          <i class="fab fa-microsoft me-1" aria-hidden="true"></i>
          {{ $t("outlook.profileTitle") }}
        </h5>
        <p v-if="status.connected" class="small text-muted mb-0">
          {{ viewingOther ? $t("outlook.mailboxOfUser") : $t("outlook.connectedAs") }}
          <strong>{{ status.emailMasked || status.email }}</strong>
        </p>
        <p v-else class="small text-muted mb-0">{{ $t("outlook.notConnected") }}</p>
      </div>
      <div class="d-flex flex-wrap gap-2">
        <button
          v-if="!status.connected && !viewingOther"
          type="button"
          class="btn btn-primary btn-sm"
          :disabled="busy || !oauthConfigured"
          @click="connectOutlook"
        >
          <i v-if="busy" class="fas fa-spinner fa-spin me-1" aria-hidden="true"></i>
          <i v-else class="fas fa-link me-1" aria-hidden="true"></i>
          {{ $t("outlook.connect") }}
        </button>
        <button
          v-if="status.connected"
          type="button"
          class="btn btn-outline-primary btn-sm"
          :disabled="busy"
          @click="syncNow"
        >
          <i v-if="syncing" class="fas fa-spinner fa-spin me-1" aria-hidden="true"></i>
          <i v-else class="fas fa-sync-alt me-1" aria-hidden="true"></i>
          {{ $t("outlook.sync") }}
        </button>
        <button
          v-if="(status.connected || status.hasStoredTokens) && !viewingOther"
          type="button"
          class="btn btn-outline-secondary btn-sm"
          :disabled="busy"
          @click="disconnect"
        >
          <i
            v-if="!status.connected"
            class="fas fa-trash-alt me-1"
            aria-hidden="true"
          ></i>
          {{
            status.connected ? $t("outlook.disconnect") : $t("outlook.unlink")
          }}
        </button>
      </div>
    </div>
    <div v-if="status.unreadableTokens" class="alert alert-warning small mb-0 py-2">
      {{ $t("outlook.unreadableTokens") }}
    </div>
    <div v-if="!oauthConfigured" class="alert alert-warning small mb-0 py-2 mt-2">
      {{ $t("outlook.oauthNotConfigured") }}
    </div>
    <div v-if="lastSync" class="small text-success mt-2 mb-0">{{ lastSync }}</div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import Swal from "sweetalert2";

export default {
  name: "OutlookProfileConnect",
  props: {
    /** Casilla que se está mirando; vacío = la propia. Solo el admin manda otra. */
    mailboxUserId: { type: String, default: "" },
  },
  data() {
    return {
      status: {
        connected: false,
        hasStoredTokens: false,
        unreadableTokens: false,
        email: "",
        emailMasked: "",
      },
      oauthConfigured: false,
      busy: false,
      syncing: false,
      lastSync: "",
    };
  },
  computed: {
    viewingOther() {
      return !!this.mailboxUserId;
    },
  },
  watch: {
    mailboxUserId() {
      this.lastSync = "";
      this.refresh();
    },
  },
  async mounted() {
    await this.refresh();
  },
  methods: {
    /** Casilla que se está mirando (vacío = la propia). */
    mailboxParams(extra = {}) {
      return this.mailboxUserId ? { ...extra, mailboxUserId: this.mailboxUserId } : { ...extra };
    },
    async refresh() {
      try {
        const [conn, cfg] = await Promise.all([
          wencesApi.get("/api/outlook/connection", { params: this.mailboxParams() }),
          wencesApi.get("/api/outlook/config"),
        ]);
        this.status = conn.data || {};
        this.oauthConfigured = !!cfg.data?.configured;
      } catch (err) {
        this.status = { connected: false, hasStoredTokens: false, unreadableTokens: false };
      }
      this.$emit("connection-changed", this.status);
    },
    async connectOutlook() {
      this.busy = true;
      try {
        const { data } = await wencesApi.get("/api/outlook/auth-url");
        if (data?.state) {
          localStorage.setItem("outlookOauthState", data.state);
        }
        if (data?.url) {
          window.location.href = data.url;
        }
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: this.$tn("Error") || "Error",
          text: err?.response?.data?.error || err.message,
        });
      } finally {
        this.busy = false;
      }
    },
    async syncNow() {
      this.syncing = true;
      this.busy = true;
      try {
        const { data } = await wencesApi.post(
          "/api/outlook/sync",
          this.mailboxParams({ limit: 50 })
        );
        this.lastSync = this.$t("outlook.syncDone", {
          created: data.created || 0,
          updated: data.updated || 0,
        });
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: this.$tn("Error") || "Error",
          text: err?.response?.data?.error || err.message,
        });
      } finally {
        this.syncing = false;
        this.busy = false;
      }
    },
    async disconnect() {
      const unlinking = !this.status.connected;
      const confirm = await Swal.fire({
        icon: "question",
        title: unlinking
          ? this.$tn("outlook.unlink")
          : this.$tn("outlook.disconnect") || "Desconectar",
        text: unlinking
          ? this.$tn("outlook.unlinkConfirm")
          : this.$tn("outlook.disconnectConfirm"),
        showCancelButton: true,
        confirmButtonText: this.$tn("Yes") || "Sí",
        cancelButtonText: this.$tn("Cancel") || "Cancelar",
      });
      if (!confirm.isConfirmed) return;
      this.busy = true;
      try {
        await wencesApi.post("/api/outlook/disconnect");
        this.lastSync = "";
        await this.refresh();
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: this.$tn("Error") || "Error",
          text: err?.response?.data?.error || err.message,
        });
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>
